import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { X, Copy, Share2, PhoneCall, MapPin, ClipboardCheck, ChevronRight, Check } from "lucide-react";
import { getSituation } from "@/data/situations";
import { useApp } from "@/context/AppContext";
import { createStatus } from "@/lib/api";
import { toast } from "sonner";

export function InstantMode({ open, situationId, onClose }) {
  const situation = getSituation(situationId);
  const { speak } = useApp();
  const [step, setStep] = useState(0); // 0 right now, 1 next, 2 share
  const [name, setName] = useState(() => localStorage.getItem("lg.name") || "");
  const [location, setLocation] = useState("");
  const [creating, setCreating] = useState(false);
  const [publicLink, setPublicLink] = useState(null);
  const [checked, setChecked] = useState({});

  useEffect(() => {
    if (open) {
      setStep(0);
      setPublicLink(null);
      setChecked({});
      // best-effort geo
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => setLocation(`Approx ${pos.coords.latitude.toFixed(3)}, ${pos.coords.longitude.toFixed(3)}`),
          () => setLocation("Location not shared"),
          { timeout: 4000, maximumAge: 60000 },
        );
      } else setLocation("Location unavailable");
    }
  }, [open]);

  useEffect(() => {
    if (!open || !situation) return;
    if (step === 0) speak(`Right now. ${situation.right_now.slice(0, 2).join(". ")}`);
    if (step === 1) speak(`Next steps. ${situation.next.slice(0, 2).join(". ")}`);
    if (step === 2) speak(`Share your status.`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, open, situationId]);

  const message = useMemo(() => {
    if (!situation) return "";
    return `LIFEGRID STATUS\n${situation.default_message}\nSituation: ${situation.label}\nLocation: ${location || "Not shared"}\nTime: ${new Date().toLocaleString()}\nStatus: Needs assistance\n— sent via LIFEGRID (demo)`;
  }, [situation, location]);

  if (!situation) return null;

  const copy = async () => {
    await navigator.clipboard.writeText(message);
    toast.success("Status message copied");
  };
  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "LIFEGRID Status", text: message });
        return;
      } catch (e) {
        /* user cancelled */
      }
    }
    copy();
  };
  const createLink = async () => {
    setCreating(true);
    try {
      const s = await createStatus({
        name: name || "Anonymous",
        situation: situation.label,
        message: situation.default_message,
        location: location || "Not shared",
      });
      if (name) localStorage.setItem("lg.name", name);
      const url = `${window.location.origin}/status/${s.slug}`;
      setPublicLink(url);
      toast.success("Public status page created");
    } catch (e) {
      toast.error("Could not create status page");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-3xl w-[calc(100vw-1rem)] p-0 border-white/10 bg-[color:var(--lg-surface)] text-[color:var(--lg-text)] overflow-hidden"
        data-testid="instant-mode-dialog"
      >
        <VisuallyHidden.Root>
          <DialogTitle>Instant Mode — {situation.label}</DialogTitle>
          <DialogDescription>Right now, next steps, and share your status.</DialogDescription>
        </VisuallyHidden.Root>
        <div className="relative">
          <div className="absolute inset-0 tac-grid-fine opacity-40 pointer-events-none" />
          <div className="relative px-5 md:px-8 pt-6 pb-4 border-b border-white/10 flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-cyan)]">
                <span className="live-dot" aria-hidden /> INSTANT MODE // {situation.id.toUpperCase()}
              </div>
              <div className="mt-2 font-display text-2xl md:text-4xl font-extrabold uppercase">{situation.label}</div>
              <div className="text-sm text-[color:var(--lg-text-2)]">{situation.subtitle}</div>
            </div>
            <button className="btn-tactile ghost !p-2" onClick={onClose} data-testid="instant-mode-close">
              <X size={16} />
            </button>
          </div>

          <div className="relative px-5 md:px-8 py-5 flex items-center gap-2 text-[10px] font-mono-tac tracking-[0.28em]">
            {["RIGHT NOW", "NEXT", "SHARE MY STATUS"].map((label, i) => (
              <button
                key={label}
                onClick={() => setStep(i)}
                data-testid={`instant-step-${i}`}
                className={`px-3 py-2 border rounded-sm transition ${
                  step === i
                    ? "border-[color:var(--lg-lime)] text-[color:var(--lg-lime)]"
                    : "border-white/10 text-[color:var(--lg-text-3)] hover:text-white"
                }`}
              >
                {String(i + 1).padStart(2, "0")} · {label}
              </button>
            ))}
          </div>

          <div className="relative px-5 md:px-8 pb-8 min-h-[320px]">
            <AnimatePresence mode="wait">
              {step === 0 && (
                <motion.div
                  key="rn"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.25 }}
                >
                  <div className="font-mono-tac text-[11px] tracking-[0.28em] text-[color:var(--lg-lime)]">RIGHT NOW</div>
                  <ol className="mt-4 space-y-3">
                    {situation.right_now.map((s, i) => (
                      <li key={i} className="flex gap-3 items-start" data-testid={`rn-step-${i}`}>
                        <span className="font-mono-tac text-[11px] tracking-[0.22em] text-[color:var(--lg-cyan)] mt-1">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className="text-base md:text-lg">{s}</span>
                      </li>
                    ))}
                  </ol>
                  <div className="mt-6 flex flex-wrap gap-2">
                    <a href="tel:112" className="btn-tactile danger" data-testid="rn-call-emergency">
                      <PhoneCall size={16} /> Call Emergency
                    </a>
                    <button className="btn-tactile primary" onClick={() => setStep(1)} data-testid="rn-next">
                      Next Steps <ChevronRight size={16} />
                    </button>
                  </div>
                </motion.div>
              )}

              {step === 1 && (
                <motion.div
                  key="nx"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.25 }}
                >
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <div className="font-mono-tac text-[11px] tracking-[0.28em] text-[color:var(--lg-cyan)]">NEXT</div>
                      <ul className="mt-4 space-y-3">
                        {situation.next.map((s, i) => (
                          <li key={i} className="flex gap-3 items-start" data-testid={`next-step-${i}`}>
                            <span className="mt-1 h-2 w-2 rounded-full bg-[color:var(--lg-cyan)]" />
                            <span>{s}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <div className="font-mono-tac text-[11px] tracking-[0.28em] text-[color:var(--lg-lime)]">CHECKLIST</div>
                      <ul className="mt-4 space-y-2">
                        {situation.checklist.map((q, i) => {
                          const on = !!checked[i];
                          return (
                            <li key={i}>
                              <button
                                onClick={() => setChecked((c) => ({ ...c, [i]: !on }))}
                                className="w-full flex items-center gap-3 border border-white/10 rounded-sm px-3 py-2 text-left hover:border-[color:var(--lg-lime)]/60"
                                data-testid={`checklist-${i}`}
                              >
                                <span
                                  className={`h-5 w-5 flex items-center justify-center rounded-sm border ${
                                    on ? "bg-[color:var(--lg-lime)] border-[color:var(--lg-lime)] text-black" : "border-white/20"
                                  }`}
                                >
                                  {on && <Check size={12} />}
                                </span>
                                <span className={on ? "line-through text-[color:var(--lg-text-3)]" : ""}>{q}</span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                  <div className="mt-6 flex flex-wrap gap-2">
                    <button className="btn-tactile" onClick={() => setStep(0)}>
                      Back
                    </button>
                    <button className="btn-tactile primary" onClick={() => setStep(2)} data-testid="next-share">
                      Share My Status <Share2 size={14} />
                    </button>
                  </div>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div
                  key="sh"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.25 }}
                >
                  <div className="grid md:grid-cols-5 gap-6">
                    <div className="md:col-span-3 glass rounded-md p-5">
                      <div className="flex items-center justify-between">
                        <div className="font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-lime)]">
                          LIFEGRID STATUS
                        </div>
                        <div className="flex items-center gap-2 font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-amber)]">
                          <span className="h-2 w-2 rounded-full bg-[color:var(--lg-amber)] animate-pulse" />
                          NEEDS ASSISTANCE
                        </div>
                      </div>
                      <div className="mt-4 font-display text-xl md:text-2xl font-bold">{situation.default_message}</div>
                      <div className="mt-4 text-sm text-[color:var(--lg-text-2)] flex items-center gap-2">
                        <MapPin size={14} className="text-[color:var(--lg-cyan)]" /> {location || "Location not shared"}
                      </div>
                      <div className="mt-1 text-sm text-[color:var(--lg-text-3)] font-mono-tac tracking-widest">
                        {new Date().toLocaleString()}
                      </div>
                      <div className="mt-6 flex flex-wrap gap-2">
                        <button className="btn-tactile primary" onClick={copy} data-testid="share-copy">
                          <Copy size={14} /> Copy Status
                        </button>
                        <button className="btn-tactile" onClick={share} data-testid="share-share">
                          <Share2 size={14} /> Share
                        </button>
                        <button className="btn-tactile ghost" onClick={onClose} data-testid="share-cancel">
                          Cancel
                        </button>
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <div className="font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-cyan)]">
                        CREATE PUBLIC STATUS PAGE
                      </div>
                      <p className="mt-2 text-sm text-[color:var(--lg-text-2)]">
                        Generate a shareable page your Trust Circle can open in one tap. You can mark yourself safe later.
                      </p>
                      <input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Your first name"
                        className="mt-3 w-full bg-transparent border border-white/15 rounded-sm px-3 py-2 focus:border-[color:var(--lg-lime)] focus:outline-none"
                        data-testid="share-name-input"
                      />
                      {!publicLink ? (
                        <button
                          className="btn-tactile primary mt-3 w-full"
                          onClick={createLink}
                          disabled={creating}
                          data-testid="share-create-link"
                        >
                          <ClipboardCheck size={14} /> {creating ? "Creating…" : "Create Link"}
                        </button>
                      ) : (
                        <div className="mt-3 space-y-2">
                          <div className="glass rounded-sm p-2 text-xs break-all" data-testid="share-public-link">
                            {publicLink}
                          </div>
                          <button
                            className="btn-tactile w-full"
                            onClick={() => {
                              navigator.clipboard.writeText(publicLink);
                              toast.success("Public link copied");
                            }}
                          >
                            <Copy size={14} /> Copy Link
                          </button>
                        </div>
                      )}
                      <p className="mt-3 text-[10px] font-mono-tac tracking-[0.24em] text-[color:var(--lg-text-3)]">
                        DEMO — LIFEGRID DOES NOT DISPATCH EMERGENCY SERVICES.
                      </p>
                    </div>
                  </div>
                  <div className="mt-6">
                    <button className="btn-tactile" onClick={() => setStep(1)}>
                      Back
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
